import './ReportBar.scss';

import { Button, IconButton, TableStatusChip, Typography } from '@trackman/web-shared-components';
import { Suspense, lazy } from 'react';

import { AppPermissions } from 'constants/AppPermissions';
import { DefaultRoutes } from 'Routes';
import { ReactComponent as RightIcon } from 'svg/ArrowRight.svg';
import { useNavigate } from 'react-router';
import { useReportId } from 'hooks/helperHooks';
import { useTranslation } from 'react-i18next';
import { useApplicationUserRightsStore } from 'stores/ApplicationUserRightsStore';
import { useReportViewModeStore } from 'stores/ReportViewModeStore';
import MobileMenu from 'components/Report/MobileMenu/MobileMenu';

const PrintReport = lazy(() => import('components/Report/PrintReport/PrintReport'));
const DownloadReport = lazy(() => import('components/Report/DownloadReport/DownloadReport'));

type Props = {
  reportName: string;
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  downloadName: string;
};

const ReportBar = ({ reportName, isEditMode, setIsEditMode, downloadName }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isCreate } = useReportId();
  const { IsAllowed } = useApplicationUserRightsStore();
  const { viewMode } = useReportViewModeStore();

  const canEditDefaultLayout = IsAllowed([AppPermissions.DefaultLayoutEdit]);

  return (
    <div className='ReportBar-container'>
      <div className={`ReportBar-title ${isEditMode ? 'is-editing' : ''}`}>
        <IconButton
          onClick={() => navigate(DefaultRoutes.Homepage, { viewTransition: true })}
          icon={() => <RightIcon style={{ transform: 'rotate(180deg)' }} />}
          className='ReportBar-icon'
        />
        <Typography variant='h2' component='div'>
          {reportName}
        </Typography>
        {isEditMode && viewMode !== 'single' && <TableStatusChip intent='info' label='Edit mode' className='ReportBar-editing-chip' />}
      </div>
      <div className='ReportBar-buttons'>
        {!isEditMode && (
          <>
            {!isCreate && (
              <>
                {viewMode === 'full' && (
                  <Suspense>
                    <PrintReport />
                  </Suspense>
                )}
                {viewMode !== 'single' && (
                  <Suspense>
                    <DownloadReport downloadName={downloadName} />
                  </Suspense>
                )}
              </>
            )}
            {(!isCreate || canEditDefaultLayout) && viewMode !== 'single' && (
              <Button className='EditReport-btn' label={t('Edit layout')} intent='secondary' onClick={() => setIsEditMode(true)} />
            )}
            {!isCreate && viewMode === 'single' && <MobileMenu downloadName={downloadName} setIsEditMode={setIsEditMode} />}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportBar;
